import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Switch from 'flarum/common/components/Switch';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

// Matches ContestValidator's MIN_OPTIONS/MAX_OPTIONS; kept in sync by hand
// since the wizard validates client-side before the server gets a say.
const MIN_OPTIONS = 2;
const MAX_OPTIONS = 6;

// Initial suggestions; managers can choose both dates in the first step.
const CARD_VISIBLE_MINUTES = 120;
const WAITING_ROOM_MINUTES = 15;

// How long to wait after the last keystroke/change before writing the draft.
const AUTOSAVE_DEBOUNCE_MS = 1500;

// The pacings offered instead of numeric fields.
const PACES = {
  normal: { questionSeconds: 20, revealSeconds: 0 },
  quick: { questionSeconds: 15, revealSeconds: 0 },
  fast: { questionSeconds: 10, revealSeconds: 0 },
};

// Matches Contest::MUSIC_TRACKS; the label is translated, the value is what
// the backend stores and the player resolves to a file.
const MUSIC_TRACKS = ['none', 'music-1', 'music-2', 'music-3', 'music-4', 'music-5', 'music-6'];

/**
 * Creating a contest from the forum, on a phone.
 *
 * The admin editor exposes every field the API accepts, including six timing
 * inputs. This keeps scheduling, questions and prizes together in three steps.
 */
export default class ContestWizard extends Page {
  oninit(vnode) {
    super.oninit(vnode);

    this.contestId = m.route.param('id') || null;
    this.editing = !!this.contestId;
    this.loading = false;
    this.locked = false;
    this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.step = 1;
    this.saving = false;
    this.errors = [];

    // Autosave never blocks the publish flow: its own request, its own
    // status, no shared `saving`/`errors` state with the explicit save.
    this.autosaveTimer = null;
    this.autosaving = false;
    this.autosaveStatus = null; // null | 'saved' | 'failed'

    // Keyed by question index: at most one upload in flight per row.
    this.imageUploading = {};
    this.imageErrors = {};

    this.title = '';
    this.description = '';
    this.startsAt = this.defaultStart();
    this.cardVisibleAt = this.shiftStart(CARD_VISIBLE_MINUTES);
    this.waitingRoomOpensAt = this.shiftStart(WAITING_ROOM_MINUTES);
    this.pace = 'normal';
    this.jokerLimit = 0;
    this.backgroundMusic = 'none';
    this.soundEffectsEnabled = true;

    this.questions = [this.blankQuestion()];
    this.prizes = [
      { place: 1, title: '', subtitle: '' },
      { place: 2, title: '', subtitle: '' },
      { place: 3, title: '', subtitle: '' },
    ];

    if (this.editing && app.forum.attribute('happyQuizCanManage')) this.loadContest();
  }

  onremove() {
    clearTimeout(this.autosaveTimer);
  }

  loadContest() {
    this.loading = true;
    return app.request({
      method: 'GET',
      url: `${app.forum.attribute('apiUrl')}/happy-quiz/admin/contests/${encodeURIComponent(this.contestId)}`,
    }).then(({ data }) => {
      this.title = data.title;
      this.description = data.description || '';
      this.timezone = data.timezone;
      this.locked = data.locked;
      this.startsAt = this.toZonedInput(data.startsAt);
      this.cardVisibleAt = this.toZonedInput(data.cardVisibleAt) || this.toZonedInput(new Date().toISOString());
      this.waitingRoomOpensAt = this.toZonedInput(data.waitingRoomOpensAt) || this.startsAt;
      this.questions = data.questions;
      this.prizes = [1, 2, 3].map((place) => data.prizes.find((prize) => prize.place === place) || { place, title: '', subtitle: '' });
      this.savedPace = { questionSeconds: data.questionSeconds, revealSeconds: data.revealSeconds };
      this.pace = Object.keys(PACES).find((key) => PACES[key].questionSeconds === data.questionSeconds && PACES[key].revealSeconds === data.revealSeconds) || 'custom';
      this.jokerLimit = data.jokerLimit || 0;
      this.backgroundMusic = MUSIC_TRACKS.includes(data.backgroundMusic) ? data.backgroundMusic : 'none';
      this.soundEffectsEnabled = data.soundEffectsEnabled !== false;
    }).catch(() => {
      this.loadFailed = true;
    }).finally(() => {
      this.loading = false;
      m.redraw();
    });
  }

  toZonedInput(iso) {
    if (!iso) return '';
    const parts = {};
    new Intl.DateTimeFormat('en-CA', {
      timeZone: this.timezone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date(iso)).forEach((part) => { parts[part.type] = part.value; });
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  }

  /**
   * Tomorrow evening, as a value a `datetime-local` input accepts. A sensible
   * default means the field can often be left alone.
   */
  defaultStart() {
    const when = new Date();

    when.setDate(when.getDate() + 1);
    when.setHours(21, 0, 0, 0);

    return this.toLocalInput(when);
  }

  shiftStart(minutes) {
    return this.toLocalInput(new Date(new Date(this.startsAt).getTime() - minutes * 60000));
  }

  toLocalInput(date) {
    const pad = (n) => String(n).padStart(2, '0');

    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
  }

  blankQuestion() {
    // Four by default — the previous fixed count — with add/remove in the UI
    // for anyone who wants fewer or more, down to MIN_OPTIONS or up to
    // MAX_OPTIONS.
    const defaultCount = 4;

    return {
      prompt: '',
      category: '',
      explanation: '',
      imageUrl: null,
      options: Array.from({ length: defaultCount }, () => ({ id: this.randomId(), text: '' })),
      correctOptionId: null,
    };
  }

  randomId() {
    return Math.random().toString(36).slice(2, 12);
  }

  view() {
    if (!app.forum.attribute('happyQuizCanManage')) {
      return (
        <div className="HappyQuiz">
          <p className="HappyQuiz-rule">{app.translator.trans('happydigital-quiz.forum.error.forbidden')}</p>
        </div>
      );
    }

    if (this.loading) return <div className="HappyQuiz"><LoadingIndicator /></div>;
    if (this.loadFailed || this.locked) return (
      <div className="HappyQuiz">
        <p className="HappyQuiz-rule">{app.translator.trans(this.locked ? 'happydigital-quiz.admin.editor.locked_notice' : 'happydigital-quiz.forum.error.generic')}</p>
      </div>
    );

    return (
      <div className="HappyQuiz HappyQuizWizard">
        {this.progress()}

        {this.step === 1 ? this.stepBasics() : null}
        {this.step === 2 ? this.stepQuestions() : null}
        {this.step === 3 ? this.stepPrizes() : null}

        {this.errors.length ? (
          <div className="HappyQuizWizard-errors">
            {this.errors.map((error) => (
              <div>{error}</div>
            ))}
          </div>
        ) : null}

        {this.footer()}
      </div>
    );
  }

  progress() {
    const t = (key, params) => app.translator.trans(`happydigital-quiz.forum.wizard.${key}`, params);

    return (
      <div className="HappyQuizWizard-progress">
        <div className="HappyQuizWizard-stepLabel">{t('step_of', { current: this.step, total: 3 })}</div>
        <div className="HappyQuizWizard-bars">
          {[1, 2, 3].map((n) => (
            <span className={'HappyQuizWizard-bar' + (n <= this.step ? ' is-done' : '')} />
          ))}
        </div>
      </div>
    );
  }

  stepBasics() {
    const t = (key, params) => app.translator.trans(`happydigital-quiz.forum.wizard.${key}`, params);

    return (
      <div>
        <h2 className="HappyQuizWizard-heading">{t(this.editing ? 'edit_heading' : 'basics_heading')}</h2>

        <div className="Form-group">
          <label>{t('title_label')}</label>
          <input
            className="FormControl"
            type="text"
            placeholder={t('title_placeholder')}
            value={this.title}
            oninput={(e) => { this.title = e.target.value; this.scheduleAutosave(); }}
          />
        </div>

        <div className="Form-group">
          <label htmlFor="HappyQuizWizard-description">{t('description_label')}</label>
          <textarea
            id="HappyQuizWizard-description"
            className="FormControl"
            rows="4"
            placeholder={t('description_placeholder')}
            value={this.description}
            oninput={(e) => { this.description = e.target.value; this.scheduleAutosave(); }}
          />
        </div>

        <div className="Form-group">
          <label>{t('start_label')}</label>
          <input
            className="FormControl"
            type="datetime-local"
            value={this.startsAt}
            oninput={(e) => { this.startsAt = e.target.value; this.scheduleAutosave(); }}
          />
          <div className="helpText">{t('timezone_help', { timezone: this.timezone })}</div>
        </div>

        <div className="Form-group">
          <label htmlFor="HappyQuizWizard-cardVisibleAt">{t('card_visible_label')}</label>
          <input
            id="HappyQuizWizard-cardVisibleAt"
            className="FormControl"
            type="datetime-local"
            value={this.cardVisibleAt}
            oninput={(e) => { this.cardVisibleAt = e.target.value; this.scheduleAutosave(); }}
          />
          <div className="helpText">{t('card_visible_help')}</div>
        </div>

        <div className="Form-group">
          <label htmlFor="HappyQuizWizard-waitingRoomOpensAt">{t('waiting_room_label')}</label>
          <input
            id="HappyQuizWizard-waitingRoomOpensAt"
            className="FormControl"
            type="datetime-local"
            value={this.waitingRoomOpensAt}
            oninput={(e) => { this.waitingRoomOpensAt = e.target.value; this.scheduleAutosave(); }}
          />
        </div>

        <div className="Form-group">
          <label>{t('pace_label')}</label>

          {/* Two pacings rather than two numeric fields: each says what it
              means in seconds, so nothing is hidden. */}
          {this.pace === 'custom' ? <p>{t('pace_detail', { question: this.savedPace.questionSeconds, reveal: this.savedPace.revealSeconds })}</p> : null}
          <div className="HappyQuizWizard-choices">
            {Object.keys(PACES).map((key) => (
              <button
                type="button"
                className={'HappyQuizWizard-choice' + (this.pace === key ? ' is-active' : '')}
                onclick={() => { this.pace = key; this.scheduleAutosave(); }}
              >
                <b>{t(`pace_${key}`)}</b>
                <span>
                  {app.translator.trans('happydigital-quiz.forum.wizard.pace_detail', {
                    question: PACES[key].questionSeconds,
                    reveal: PACES[key].revealSeconds,
                  })}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="Form-group">
          <label htmlFor="HappyQuizWizard-jokerLimit">{t('joker_limit_label')}</label>
          <input
            id="HappyQuizWizard-jokerLimit"
            className="FormControl"
            type="number"
            min="0"
            max="10"
            value={this.jokerLimit}
            oninput={(e) => { this.jokerLimit = parseInt(e.target.value, 10) || 0; this.scheduleAutosave(); }}
          />
          <div className="helpText">{t('joker_limit_help')}</div>
        </div>

        <div className="Form-group">
          <label htmlFor="HappyQuizWizard-backgroundMusic">{t('background_music_label')}</label>
          <select
            id="HappyQuizWizard-backgroundMusic"
            className="FormControl"
            value={this.backgroundMusic}
            onchange={(e) => { this.backgroundMusic = e.target.value; this.scheduleAutosave(); }}
          >
            {MUSIC_TRACKS.map((track) => (
              <option value={track}>{t(`background_music_${track.replace('-', '_')}`)}</option>
            ))}
          </select>
          <div className="helpText">{t('background_music_help')}</div>
        </div>

        <div className="Form-group">
          <Switch
            state={this.soundEffectsEnabled}
            onchange={(value) => { this.soundEffectsEnabled = value; this.scheduleAutosave(); }}
          >
            {t('sound_effects_label')}
          </Switch>
          <div className="helpText">{t('sound_effects_help')}</div>
        </div>
      </div>
    );
  }

  stepQuestions() {
    const t = (key, params) => app.translator.trans(`happydigital-quiz.forum.wizard.${key}`, params);

    return (
      <div>
        <h2 className="HappyQuizWizard-heading">{t('questions_heading')}</h2>
        <div className="helpText">{t('questions_help')}</div>

        {this.questions.map((question, index) => (
          <div className="HappyQuizWizard-question">
            <div className="HappyQuizWizard-questionHead">
              <b>{t('question_number', { number: index + 1 })}</b>
              {this.questions.length > 1 ? (
                <button
                  type="button"
                  className="Button Button--text HappyQuizWizard-remove"
                  onclick={() => this.removeQuestion(index)}
                >
                  {t('remove')}
                </button>
              ) : null}
            </div>

            <textarea
              className="FormControl"
              rows="2"
              placeholder={t('prompt_placeholder')}
              value={question.prompt}
              oninput={(e) => this.updateQuestion(index, { prompt: e.target.value })}
            />

            {this.imageField(question, index)}

            {/* Tapping a row's letter marks it correct, so choosing the answer
                is one tap rather than a separate control. */}
            <div className="HappyQuizWizard-options">
              {question.options.map((option, optionIndex) => (
                <div className="HappyQuizWizard-option">
                  <button
                    type="button"
                    className={
                      'HappyQuizWizard-letter' + (question.correctOptionId === option.id ? ' is-correct' : '')
                    }
                    title={t('mark_correct')}
                    aria-pressed={question.correctOptionId === option.id}
                    onclick={() => this.updateQuestion(index, { correctOptionId: option.id })}
                  >
                    {LETTERS[optionIndex]}
                  </button>
                  <input
                    className="FormControl"
                    type="text"
                    placeholder={t('option_placeholder')}
                    value={option.text}
                    oninput={(e) => this.updateOption(index, optionIndex, e.target.value)}
                  />
                  {question.options.length > MIN_OPTIONS ? (
                    <button
                      type="button"
                      className="Button Button--icon Button--text HappyQuizWizard-removeOption"
                      title={t('remove_option')}
                      aria-label={t('remove_option')}
                      onclick={() => this.removeOption(index, optionIndex)}
                    >
                      <i className="fas fa-times" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              ))}
            </div>

            {question.options.length < MAX_OPTIONS ? (
              <button
                type="button"
                className="Button Button--text HappyQuizWizard-addOption"
                onclick={() => this.addOption(index)}
              >
                {t('add_option')}
              </button>
            ) : null}

            <div className="HappyQuizWizard-hint">
              {question.correctOptionId
                ? t('correct_is', {
                    letter: LETTERS[question.options.findIndex((o) => o.id === question.correctOptionId)],
                  })
                : t('pick_correct')}
            </div>
          </div>
        ))}

        <button type="button" className="Button HappyQuizWizard-add" onclick={() => this.addQuestion()}>
          {t('add_question')}
        </button>
      </div>
    );
  }

  /**
   * Optional image for one question. Uploads through Mutluresim (if that
   * extension is installed) and stores only the resulting URL — the image
   * itself lives on Mutluresim's CDN, already hosted by the time the contest
   * goes live, so a participant's browser just requests an ordinary external
   * image rather than something this server streams.
   */
  imageField(question, index) {
    const t = (key, params) => app.translator.trans(`happydigital-quiz.forum.wizard.${key}`, params);

    if (!app.forum.attribute('mutluresimCanUpload')) {
      return null;
    }

    const uploading = !!this.imageUploading[index];
    const error = this.imageErrors[index];

    return (
      <div className="HappyQuizWizard-image">
        {question.imageUrl ? (
          <div className="HappyQuizWizard-imagePreview">
            <img src={question.imageUrl} alt="" />
            <button
              type="button"
              className="Button Button--icon Button--text HappyQuizWizard-imageRemove"
              title={t('remove_image')}
              aria-label={t('remove_image')}
              disabled={uploading}
              onclick={() => this.updateQuestion(index, { imageUrl: null })}
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </div>
        ) : (
          <label className="HappyQuizWizard-imageUpload">
            <i className="fas fa-image" aria-hidden="true" />
            <span>{uploading ? t('image_uploading') : t('add_image')}</span>
            <input
              type="file"
              accept="image/*"
              disabled={uploading}
              onchange={(e) => this.uploadQuestionImage(index, e.target.files[0])}
            />
          </label>
        )}

        {error ? <div className="HappyQuizWizard-imageError">{error}</div> : null}
      </div>
    );
  }

  uploadQuestionImage(index, file) {
    if (!file) return;

    this.imageUploading = { ...this.imageUploading, [index]: true };
    this.imageErrors = { ...this.imageErrors, [index]: null };
    m.redraw();

    const formData = new FormData();
    formData.append('source', file);

    app
      .request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/mutluresim/upload`,
        serialize: (data) => data,
        body: formData,
      })
      .then((response) => {
        this.imageUploading = { ...this.imageUploading, [index]: false };

        if (response.status_code === 200 && response.image && response.image.url) {
          this.updateQuestion(index, { imageUrl: response.image.url });
        } else {
          this.imageErrors = {
            ...this.imageErrors,
            [index]: response.message || app.translator.trans('happydigital-quiz.forum.wizard.image_upload_failed'),
          };
          m.redraw();
        }
      })
      .catch(() => {
        this.imageUploading = { ...this.imageUploading, [index]: false };
        this.imageErrors = {
          ...this.imageErrors,
          [index]: app.translator.trans('happydigital-quiz.forum.wizard.image_upload_failed'),
        };
        m.redraw();
      });
  }

  stepPrizes() {
    const t = (key, params) => app.translator.trans(`happydigital-quiz.forum.wizard.${key}`, params);

    return (
      <div>
        <h2 className="HappyQuizWizard-heading">{t('prizes_heading')}</h2>
        <div className="helpText">{t('prizes_help')}</div>

        {this.prizes.map((prize, index) => (
          <div className="Form-group">
            <label>{t('prize_place', { place: prize.place })}</label>
            <input
              className="FormControl"
              type="text"
              placeholder={t('prize_placeholder')}
              value={prize.title}
              oninput={(e) => this.updatePrize(index, { title: e.target.value })}
            />
          </div>
        ))}

        <div className="HappyQuizWizard-summary">
          <b>{t('summary_heading')}</b>
          <div>{t('summary_title', { title: this.title || t('summary_untitled') })}</div>
          <div>{t('summary_questions', { count: this.questions.length })}</div>
          <div>{t('summary_duration', { duration: this.durationText() })}</div>
        </div>
      </div>
    );
  }

  durationText() {
    const pace = PACES[this.pace] || this.savedPace;
    const total = this.questions.length * (pace.questionSeconds + pace.revealSeconds);

    const minutes = Math.floor(total / 60);
    const seconds = total % 60;

    return app.translator.trans('happydigital-quiz.forum.wizard.duration_value', { minutes, seconds });
  }

  footer() {
    const t = (key, params) => app.translator.trans(`happydigital-quiz.forum.wizard.${key}`, params);

    return (
      <div className="HappyQuizWizard-footer">
        {this.autosaveNote()}

        {this.step > 1 ? (
          <button type="button" className="Button" disabled={this.saving} onclick={() => this.back()}>
            {t('back')}
          </button>
        ) : (
          <span />
        )}

        {this.step < 3 ? (
          <button type="button" className="Button Button--primary" onclick={() => this.next()}>
            {t('next')}
          </button>
        ) : (
          <button
            type="button"
            className="Button Button--primary"
            disabled={this.saving}
            onclick={() => this.publish()}
          >
            {this.saving ? <LoadingIndicator display="inline" size="small" /> : t(this.editing ? 'save' : 'publish')}
          </button>
        )}
      </div>
    );
  }

  /**
   * A quiet status note beside the footer buttons — never an alert, since
   * autosaving is meant to be invisible when it works.
   */
  autosaveNote() {
    const t = (key) => app.translator.trans(`happydigital-quiz.forum.wizard.${key}`);

    if (this.autosaving) {
      return <span className="HappyQuizWizard-autosave">{t('autosave_saving')}</span>;
    }

    if (this.autosaveStatus === 'saved') {
      return <span className="HappyQuizWizard-autosave">{t('autosave_saved')}</span>;
    }

    if (this.autosaveStatus === 'failed') {
      return <span className="HappyQuizWizard-autosave is-failed">{t('autosave_failed')}</span>;
    }

    return null;
  }

  back() {
    this.errors = [];
    this.step = Math.max(1, this.step - 1);
  }

  /**
   * Validates the current step before moving on, so a mistake is caught beside
   * the field that caused it rather than at the end.
   */
  next() {
    const errors = this.validateStep(this.step);

    if (errors.length) {
      this.errors = errors;
      return;
    }

    this.errors = [];
    this.step = Math.min(3, this.step + 1);
  }

  validateStep(step) {
    const t = (key, params) => app.translator.trans(`happydigital-quiz.forum.wizard.error_${key}`, params);
    const errors = [];

    if (step === 1) {
      if (!this.title.trim()) errors.push(t('title'));

      if (!this.startsAt || !Number.isFinite(new Date(this.startsAt).getTime())) {
        errors.push(t('start'));
      } else if (this.startsAt <= this.toZonedInput(new Date().toISOString())) {
        errors.push(t('start_past'));
      }
    }

    if (step === 1) {
      const card = new Date(this.cardVisibleAt).getTime();
      const waiting = new Date(this.waitingRoomOpensAt).getTime();
      const start = new Date(this.startsAt).getTime();

      if (!Number.isFinite(card)) errors.push(t('card_visible'));
      if (!Number.isFinite(waiting)) errors.push(t('waiting_room'));
      if (card > waiting || waiting > start || card > start) errors.push(t('schedule_order'));
    }

    if (step === 2) {
      this.questions.forEach((question, index) => {
        const number = index + 1;

        if (!question.prompt.trim()) {
          errors.push(t('prompt', { number }));
        }

        if (question.options.some((o) => !o.text.trim())) {
          errors.push(t('options', { number }));
        }

        if (!question.correctOptionId) {
          errors.push(t('correct', { number }));
        }
      });
    }

    return errors;
  }

  addQuestion() {
    this.questions = [...this.questions, this.blankQuestion()];
    this.scheduleAutosave();
  }

  removeQuestion(index) {
    this.questions = this.questions.filter((_, i) => i !== index);
    this.scheduleAutosave();
  }

  updateQuestion(index, changes) {
    this.questions = this.questions.map((q, i) => (i === index ? { ...q, ...changes } : q));
    this.scheduleAutosave();
  }

  updateOption(questionIndex, optionIndex, text) {
    this.questions = this.questions.map((question, i) => {
      if (i !== questionIndex) return question;

      const options = question.options.map((o, j) => (j === optionIndex ? { ...o, text } : o));

      return { ...question, options };
    });
    this.scheduleAutosave();
  }

  addOption(questionIndex) {
    this.questions = this.questions.map((question, i) => {
      if (i !== questionIndex || question.options.length >= MAX_OPTIONS) return question;

      return { ...question, options: [...question.options, { id: this.randomId(), text: '' }] };
    });
    this.scheduleAutosave();
  }

  removeOption(questionIndex, optionIndex) {
    this.questions = this.questions.map((question, i) => {
      if (i !== questionIndex || question.options.length <= MIN_OPTIONS) return question;

      const removed = question.options[optionIndex];
      const options = question.options.filter((_, j) => j !== optionIndex);

      return {
        ...question,
        options,
        // The removed option can no longer be "the" answer; a stale id here
        // would point at nothing once saved.
        correctOptionId: question.correctOptionId === removed.id ? null : question.correctOptionId,
      };
    });
    this.scheduleAutosave();
  }

  updatePrize(index, changes) {
    this.prizes = this.prizes.map((p, i) => (i === index ? { ...p, ...changes } : p));
    this.scheduleAutosave();
  }

  /**
   * The draft payload shared between the explicit publish/save and the
   * background autosave, so the two can never drift apart.
   */
  buildPayload() {
    const pace = PACES[this.pace] || this.savedPace;

    return {
      title: this.title.trim(),
      description: this.description.trim() || null,
      // The browser's own zone: the person picking the time is in it.
      timezone: this.timezone,
      startsAt: this.startsAt,
      cardVisibleAt: this.cardVisibleAt,
      waitingRoomOpensAt: this.waitingRoomOpensAt,
      questionSeconds: pace.questionSeconds,
      revealSeconds: pace.revealSeconds,
      jokerLimit: Math.max(0, Math.min(10, this.jokerLimit || 0)),
      backgroundMusic: MUSIC_TRACKS.includes(this.backgroundMusic) ? this.backgroundMusic : 'none',
      soundEffectsEnabled: this.soundEffectsEnabled,
      questions: this.questions,
      prizes: this.prizes.filter((p) => p.title.trim() !== ''),
    };
  }

  /**
   * Autosaves the draft a short while after the manager stops typing, so a
   * closed tab or a crashed browser never costs more than the last debounce
   * window of work. Silent by design: a failed autosave shows a small status
   * note rather than interrupting with an alert, and the next keystroke
   * simply tries again.
   */
  scheduleAutosave() {
    if (this.locked || this.loadFailed) return;

    clearTimeout(this.autosaveTimer);
    this.autosaveTimer = setTimeout(() => this.autosaveNow(), AUTOSAVE_DEBOUNCE_MS);
  }

  autosaveNow() {
    // A draft with no title yet is not worth a round trip; everything else
    // the backend is happy to store half-filled.
    if (!this.title.trim() || this.saving || this.autosaving || this.locked || this.loadFailed) return;

    this.autosaving = true;
    m.redraw();

    return app
      .request({
        method: this.contestId ? 'PATCH' : 'POST',
        url: `${app.forum.attribute('apiUrl')}/happy-quiz/admin/contests${this.contestId ? `/${this.contestId}` : ''}`,
        body: { data: this.buildPayload() },
      })
      .then((response) => {
        if (!this.contestId) this.contestId = response.data.id;
        this.autosaving = false;
        this.autosaveStatus = 'saved';
        m.redraw();
      })
      .catch(() => {
        this.autosaving = false;
        this.autosaveStatus = 'failed';
        m.redraw();
      });
  }

  /**
   * Saves the draft and publishes it in one go: from here a contest is either
   * ready to run or not worth keeping.
   */
  publish() {
    if (this.saving || this.autosaving || this.loading || this.locked || this.loadFailed) return;

    const errors = [...this.validateStep(1), ...this.validateStep(2)];

    if (errors.length) {
      this.errors = errors;
      // Send the user back to the step that needs fixing.
      this.step = this.validateStep(1).length ? 1 : 2;
      return;
    }

    // The explicit save always wins over a pending autosave, so the two
    // requests never race and overwrite each other's response.
    clearTimeout(this.autosaveTimer);

    this.saving = true;
    this.errors = [];

    return app
      .request({
        method: this.contestId ? 'PATCH' : 'POST',
        url: `${app.forum.attribute('apiUrl')}/happy-quiz/admin/contests${this.contestId ? `/${this.contestId}` : ''}`,
        body: { data: this.buildPayload() },
      })
      .then((response) => {
        const contest = response.data;
        this.contestId = contest.id;
        if (contest.status === 'scheduled') return contest;

        return app
          .request({
            method: 'POST',
            url: `${app.forum.attribute('apiUrl')}/happy-quiz/admin/contests/${contest.id}/publish`,
          })
          .then(() => contest);
      })
      .then((contest) => {
        this.saving = false;

        app.alerts.show({ type: 'success' }, app.translator.trans(`happydigital-quiz.forum.wizard.${this.editing ? 'saved' : 'published'}`));

        m.route.set(app.route('happyQuiz.show', { slug: contest.slug }));
      })
      .catch((error) => {
        this.saving = false;

        // The publish endpoint returns exactly what is missing.
        const detail = error?.response?.errors?.[0]?.detail;
        const code = error?.response?.errors?.[0]?.code;

        this.errors = Array.isArray(detail)
          ? detail.map((code) => app.translator.trans(`happydigital-quiz.admin.validation.${code}`))
          : [app.translator.trans(code === 'contest_locked' ? 'happydigital-quiz.admin.editor.locked_notice' : 'happydigital-quiz.forum.error.generic')];

        m.redraw();
      });
  }
}
